"use client"

import { useState, useEffect, useCallback } from "react"
import { useRouter } from "next/navigation"
import { loadStripe } from "@stripe/stripe-js"
import { motion } from "framer-motion"
import { Check, Shield, Zap, MessageCircle, Star } from "lucide-react"
import Logo from "../../components/Logo"
import { trackEvent } from "../../lib/analytics"

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY)

const testimonials = [
  { text: "Mejoré mi pronunciación y comprensión más rápido de lo que esperaba.", name: "Maria", flag: "🇪🇸" },
  { text: "¡Es como tener un compañero de idiomas disponible 24/7!", name: "Luca", flag: "🇮🇹" },
  { text: "Nunca pensé que aprender un idioma pudiera ser tan divertido e interactivo.", name: "Rosa", flag: "🇦🇷" },
]

const premiumFeatures = [
  "Conversaciones ilimitadas",
  "14 idiomas disponibles",
  "Todos los temas y escenarios",
  "Traducción con un clic en cualquier palabra",
  "Historial y memoria de conversaciones",
  "Seguimiento de progreso y analíticas",
  "Procesamiento de voz prioritario",
]

export default function SubscribePage() {
  const router = useRouter()
  const [loadingPlan, setLoadingPlan] = useState(null)

  const monthlyPrice = 11.99
  const yearlyPrice = 59
  const yearlyMonthly = Math.round((yearlyPrice / 12) * 100) / 100
  const savingsPercent = Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100)

  const handleSubscribe = useCallback(async (plan) => {
    const priceId = plan === "monthly"
      ? process.env.NEXT_PUBLIC_STRIPE_MONTHLY_PRICE_ID
      : process.env.NEXT_PUBLIC_STRIPE_ANNUAL_PRICE_ID
    setLoadingPlan(plan)
    trackEvent("subscribe_click", { plan })
    try {
      const stripe = await stripePromise
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId }),
      })
      const { sessionId } = await response.json()
      await stripe.redirectToCheckout({ sessionId })
    } catch {
      setLoadingPlan(null)
    }
  }, [])

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="py-6 px-6 flex items-center justify-between max-w-5xl mx-auto">
        <button onClick={() => router.push("/")} className="hover:opacity-80 transition">
          <Logo size={32} />
        </button>
        <button
          onClick={() => router.push("/app")}
          className="text-sm text-slate-400 hover:text-white transition"
        >
          Volver a la app
        </button>
      </header>

      {/* Hero */}
      <section className="px-6 pt-8 pb-12 text-center max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">
            Tu prueba gratuita ha terminado
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            No pierdas tu progreso.{" "}
            <span className="text-blue-400">Sigue practicando.</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Ya diste el primer paso. Suscríbete para seguir mejorando con
            conversaciones ilimitadas, todos los idiomas y tu tutor personalizado.
          </p>
        </motion.div>
      </section>

      {/* Pricing cards */}
      <section className="px-6 pb-16 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* Monthly */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="border border-slate-700 rounded-xl p-7"
          >
            <h3 className="text-base font-semibold text-white">Mensual</h3>
            <div className="mt-3 mb-1">
              <span className="text-4xl font-bold tracking-tight">${monthlyPrice}</span>
              <span className="text-slate-400 text-sm ml-1">/mes</span>
            </div>
            <p className="text-sm text-slate-400 mb-5 mt-3">
              Ideal para probar sin compromiso a largo plazo. Cancela cuando quieras.
            </p>
            <button
              onClick={() => handleSubscribe("monthly")}
              disabled={loadingPlan === "monthly"}
              className="w-full py-2.5 rounded-full border border-slate-600 text-white font-semibold text-sm hover:bg-slate-800 transition duration-150 disabled:opacity-50"
            >
              {loadingPlan === "monthly" ? "Procesando..." : "Suscribirse"}
            </button>
            <ul className="mt-6 space-y-2.5">
              {premiumFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-400">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Annual */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="border-2 border-blue-600 rounded-xl p-7 relative bg-blue-900/20"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-0.5 rounded-full whitespace-nowrap">
              Más Popular · Ahorra {savingsPercent}%
            </div>
            <h3 className="text-base font-semibold text-white">Anual</h3>
            <div className="mt-3 mb-1">
              <span className="text-4xl font-bold tracking-tight">${yearlyMonthly}</span>
              <span className="text-slate-400 text-sm ml-1">/mes</span>
            </div>
            <p className="text-xs text-blue-400 font-medium mb-3">
              Facturado ${yearlyPrice}/año
            </p>
            <p className="text-sm text-slate-400 mb-5">
              El plan de quienes van en serio. Práctica diaria al mejor precio.
            </p>
            <button
              onClick={() => handleSubscribe("annual")}
              disabled={loadingPlan === "annual"}
              className="w-full py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition duration-150 disabled:opacity-50"
            >
              {loadingPlan === "annual" ? "Procesando..." : "Suscribirse"}
            </button>
            <ul className="mt-6 space-y-2.5">
              {premiumFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Social proof */}
      <section className="px-6 pb-16 max-w-3xl mx-auto">
        <h2 className="text-center text-lg font-semibold text-white mb-8">
          Lo que dicen nuestros estudiantes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 + idx * 0.1 }}
              className="border border-slate-800 rounded-xl p-5"
            >
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <p className="text-sm text-slate-300 mb-3">&ldquo;{t.text}&rdquo;</p>
              <p className="text-xs text-slate-500">{t.name} {t.flag}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust bar */}
      <section className="px-6 pb-16">
        <div className="max-w-2xl mx-auto flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
          <span className="flex items-center gap-1.5">
            <Shield className="w-4 h-4" /> Pago seguro con Stripe
          </span>
          <span className="flex items-center gap-1.5">
            <Zap className="w-4 h-4" /> Acceso inmediato
          </span>
          <span className="flex items-center gap-1.5">
            <MessageCircle className="w-4 h-4" /> Cancela cuando quieras
          </span>
        </div>
      </section>
    </div>
  )
}
